import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'

export interface Reward {
  id: string
  name: string
  description: string
  cost_points: number
  // Free text; group with categoryKey() from utils/rewardCategories.
  category: string
  availability: 'always' | 'limited' | 'seasonal'
  stock_remaining?: number
  icon: string
  value_in_cents?: number
  requires_parent_approval: boolean
  active: boolean
  // Limited Quantity: limit_count redemptions per kid per limit_period.
  limit_count?: number | null
  limit_period?: 'day' | 'week' | 'month' | null
  // The signed-in kid's redemptions in the current period (0 without a limit).
  period_redemption_count?: number
}

/** Redemptions left this period for a limited reward, or null without a limit. */
export function remainingThisPeriod(reward: Reward): number | null {
  if (!reward.limit_count || !reward.limit_period) return null
  return Math.max(0, reward.limit_count - (reward.period_redemption_count ?? 0))
}

export interface ExchangeRate {
  configured: boolean
  points_per_dollar: number
}

/**
 * The family's points-to-money rate. `configured` is false until a parent
 * sets one under Rewards > Manage Rewards.
 */
export function useExchangeRate() {
  return useQuery({
    queryKey: [...rewardsKeys.all, 'exchange-rate'],
    queryFn: async () => (await apiClient.get('/api/exchange-rate')) as ExchangeRate,
    staleTime: 5 * 60 * 1000,
  })
}

export interface RewardRedemption {
  id: string
  user_id: string
  reward_id: string
  points_spent: number
  status: 'pending' | 'approved' | 'delivered' | 'rejected'
  created_at: string
  approved_at?: string
  fulfilled_at?: string
  notes?: string
  reward: Reward
}

export interface GreenlightTransfer {
  id: string
  user_id: string
  points_converted: number
  dollar_amount: number
  payment_method: string
  notes?: string
  created_at: string
}

// Query keys factory
export const rewardsKeys = {
  all: ['rewards'] as const,
  list: () => [...rewardsKeys.all, 'list'] as const,
  redemptions: () => [...rewardsKeys.all, 'redemptions'] as const,
  transfers: (userId: string) => [...rewardsKeys.all, 'transfers', userId] as const,
}

/**
 * Fetch available rewards
 */
export function useRewards() {
  return useQuery({
    queryKey: rewardsKeys.list(),
    queryFn: async () => {
      const data = await apiClient.getRewards()
      return (data || []) as Reward[]
    },
    staleTime: 60 * 1000, // 1 minute - rewards don't change often
  })
}

/**
 * Fetch user's redemption history
 */
export function useRedemptions() {
  return useQuery({
    queryKey: rewardsKeys.redemptions(),
    queryFn: async () => {
      const data = await apiClient.getMyRedemptions()
      return (data || []) as RewardRedemption[]
    },
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Fetch Greenlight transfers for a user
 */
export function useGreenlightTransfers(userId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: rewardsKeys.transfers(userId || ''),
    queryFn: async () => {
      if (!userId) return []
      const response = await apiClient.get('/api/conversions/history')
      const conversionsArray = Array.isArray(response) ? response :
        (response?.conversions && Array.isArray(response.conversions)) ? response.conversions : []
      // Filter to only show current user's transfers
      return conversionsArray.filter((c: GreenlightTransfer) => c.user_id === userId)
    },
    enabled: enabled && !!userId,
    staleTime: 30 * 1000,
  })
}

/**
 * Redeem a reward
 */
export function useRedeemReward() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { reward_id: string; notes?: string }) => {
      return await apiClient.redeemReward(params)
    },
    onSuccess: () => {
      // Invalidate all rewards-related queries
      queryClient.invalidateQueries({ queryKey: rewardsKeys.all })
    },
  })
}
